import { Link } from "@tanstack/react-router";
import { SITE, LEGAL_ROUTES, PRIMARY_NAV_GROUPS } from "@/lib/routes";
import { Logo } from "@/components/Logo";

// The footer link columns mirror the header navigation exactly — same source
// (PRIMARY_NAV_GROUPS) — minus Guides, which gets a single link in the bottom
// bar instead of its own column. At most MAX_LINKS tools are listed per column;
// the full set is one click away via the "View all" link to the category hub.
const MAX_LINKS = 5;

type NavGroup = (typeof PRIMARY_NAV_GROUPS)[number];

export function Footer() {
  const columns = PRIMARY_NAV_GROUPS.filter((g) => g.hubPath !== "/barcode-guides");
  const year = new Date().getFullYear();

  return (
    // "entry-footer": Journey's default sidebar stop selector skips
    // footer.entry-footer, so the rail stops at #mv-sidebar-stop (ToolLayout)
    // instead of running down to this footer.
    <footer
      className="entry-footer mt-24 border-t border-border bg-muted/30"
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        Site footer
      </h2>

      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-4 py-12 lg:flex-row lg:gap-12">
        {/* Brand */}
        <div className="lg:w-56 lg:shrink-0">
          <Link
            to="/"
            aria-label={`${SITE.name}.com home`}
            className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Logo className="h-7 w-auto" />
          </Link>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Free browser-based barcode &amp; QR tools — read, generate, validate and decode UPC, EAN,
            GTIN, ISBN, QR and GS1 codes.
          </p>
        </div>

        {/* Link columns */}
        <div className="grid flex-1 grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-4">
          {columns.map((col) => (
            <FooterCol key={col.label} col={col} />
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 px-4 py-6 text-xs text-muted-foreground md:flex-row md:items-center">
          <p>
            © {year} {SITE.name}.com — All rights reserved.
          </p>
          {/* Journey's "Update privacy settings" link is inserted right after
              this marker (dashboard Footer Selector: #mv-privacy-link). */}
          <span id="mv-privacy-link" className="hidden" />
          <nav aria-label="Footer" className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link
              to="/read-barcode-app"
              className="rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              App
            </Link>
            <Link
              to="/barcode-guides"
              className="rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Guides
            </Link>
            {LEGAL_ROUTES.map((r) => (
              <Link
                key={r.path}
                to={r.path}
                className="rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {r.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ col }: { col: NavGroup }) {
  const visible = col.items.slice(0, MAX_LINKS);

  return (
    <nav aria-label={col.label}>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">{col.label}</h3>
      <ul className="mt-4 space-y-2.5 text-sm">
        {visible.map((l) => (
          <li key={l.path}>
            <Link
              to={l.path}
              className="rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {l.label}
            </Link>
          </li>
        ))}
        {col.items.length > MAX_LINKS && col.hubPath && (
          <li className="pt-0.5">
            <Link
              to={col.hubPath}
              className="inline-flex items-center gap-1 rounded-sm font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              View all
              <span aria-hidden="true">→</span>
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}
